import { Component } from '@angular/core';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { EnaioAssignment } from 'src/modules/enaio/shared/EnaioAssignment';
import { EnaioCallInsert } from 'src/modules/enaio/shared/EnaioCallInsert';
import { EnaioDocument } from 'src/modules/enaio/shared/EnaioDocument';
import { EnaioLocation } from 'src/modules/enaio/shared/EnaioLocation';
import { EnaioObjectId } from 'src/modules/enaio/shared/EnaioObjectId';
import { EnaioWorkflowModel } from 'src/modules/enaio/shared/EnaioWorkflowModel';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { SmTimer } from 'src/modules/sm-base/models/sm-timer.model';
import { FrontendFieldDefinition } from 'src/modules/sm-base/models/frontend-field-definition.model';
import { FrontendFieldListItem } from 'src/modules/sm-base/models/frontend-field-list-item.model';
import { FrontendFieldType } from 'src/modules/sm-base/models/frontend-field-type.enum';
import { FrontendFormDefinition } from 'src/modules/sm-base/models/frontend-form-definition.model';
import { TableCellType } from 'src/modules/sm-base/models/table-cell-type.enum';
import { TableColumn } from 'src/modules/sm-base/models/table-column.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { EnaioInboxDto } from '../../models/enaio-inbox-dto.model';
import { FrontendWorkflowDefinition } from '../../models/frontend-workflow-definition.model';
import { PortalWorkflowUser } from '../../models/portal-workflow-user.model';
import { LoginUser } from 'src/modules/sm-base/shared/login-user.model';
import { environment } from 'src/environments/environment';
import { Config } from 'src/modules/app-template/models/config.model';
import { EnaioWorkflowsConfig } from '../../enaio-workflows.config';

@Component({
    selector: 'enaio-workflows-main',
    templateUrl: './enaio-workflows-main.component.html',
    styleUrls: ['enaio-workflows-main.component.scss']
})
export class EnaioWorkflowsMainComponent extends ComponentView {

    loading = true;
    startables: EnaioWorkflowModel[] = [];
    inbox: EnaioInboxDto;
    inboxTables: OrdinaryObject<TableData>;
    anyWorkflows = true;
    atWork = true;
    substituteDuringAbsence: PortalWorkflowUser;
    workflowDefs: FrontendWorkflowDefinition[];
    personellFile: EnaioDocument;
    personell: PortalWorkflowUser[];
    thisYear = new Date().getFullYear(); 
    setAbsentInProgress = false;
    fd = Config.get().getModuleConfig(EnaioWorkflowsConfig).fd;

    async initParams(): Promise<boolean> {
        this.app.updateNavigation("Portal", []);

        this.workflowDefs = await RestEndpoint.main().run("api/wfl/workflow/definitions").list(FrontendWorkflowDefinition);
        this.startables = await RestEndpoint.main().run("api/wfl/workflow/startable").list(EnaioWorkflowModel);
        
        if (this.fd) {
            this.personellFile = await RestEndpoint.main().run("api/wfl/tbr/getPersonellFile").get(EnaioDocument);
            this.personell = (await RestEndpoint.main().run("api/wfl/tbr/getPersonell2").list(PortalWorkflowUser)).filter(u => u.folderId != this.personellFile.id);
            if (!this.app.auth.userDetails.getDatabaseUser().getAttributeValueBool("zeitwirtschaft")) {
                this.startables = this.startables.filter(s => s.instanceName != "Gleitzeitantrag");
            }
        }
        
        this.startables = Utils.arraySortBy(this.startables, s => s.name);

        this.addTimer(new SmTimer(120_000, 120_000, this.refresh.bind(this), true));
        await this.refresh();

        this.loading = false;

        return true;
    }

    async refreshInbox(): Promise<void> {
        this.inbox = await RestEndpoint.main().run("api/wfl/workflow/inbox").get(EnaioInboxDto);
        this.inbox.translate(this.app);

        this.inboxTables = {};
        for (let inbox of this.inbox.inboxes) {
            this.inboxTables[inbox.id] = new TableData(inbox.columns.map(column => new TableColumn(column.id, column.title, column.type, { flex: 1 })),
                this.inbox.processes.filter(p => p.inboxId == inbox.id).map(wi => {
                    let values: OrdinaryObject = {};
                    for (let col of inbox.columns) {
                        values[col.id] = TableCellType.convertValue(this.app.tRegex("wfs.inboxes." + inbox.id + ".columnContentTranslation." + col.id, wi.columns[col.id]), col.type);
                    }
                    return new TableRow("", wi, values, { styleClass: "mouse-pointer" });
                }));
        }
        this.anyWorkflows = Utils.objectGetValues(this.inboxTables).some(table => table.rows.length > 0);
    }

    async refresh(): Promise<void> {
        await this.refreshInbox();
        if (this.fd) {
            let result = await RestEndpoint.main().run("api/wfl/workflow/atWork2").getObject();
            this.atWork = result.atWork && !this.setAbsentInProgress;
            this.substituteDuringAbsence = result.substituteDuringAbsence;
        }
    }

    async selectRow(event): Promise<void> {
        await this.app.navigateTo(["/enaio-workflows", "workitem", event.raw.id]);
    }

    async changeAtWork(): Promise<void> {
        if (!this.atWork) {
            if (this.fd) {
                this.setAbsentInProgress = true;
                try {
                    let allUsers = await RestEndpoint.main().query({ superiorsOnly: false }).run("api/wfl/tbr/getAllSuperiors").list(PortalWorkflowUser); 
                    let superiorUserName = this.app.auth.userDetails.getDatabaseUser().getAttributeValue("superiorUserName");
                    let form = this.app.tForm("customers.formeld.setAbsent", new FrontendFormDefinition([
                        new FrontendFieldDefinition("defaultSubstitute", this.app.t("customers.formeld.setAbsent.defaultSubstitute") + " (" + superiorUserName + ")", FrontendFieldType.checkBox, { value: true, onValueChanged: () => {
                            form.getField("substitute").visible = !(form.getValue("defaultSubstitute") as boolean);
                        }}),
                        new FrontendFieldDefinition("substitute", "", FrontendFieldType.comboBox, { 
                            visible: false,
                            mandatory: true,
                            dropdownEditable: false, 
                            value: "",
                            listItems: FrontendFieldListItem.sort([new FrontendFieldListItem("", ""), ...allUsers.map(user => new FrontendFieldListItem(user.userName, user.lastName + ", " + user.firstName))])})
                    ]));
                    if (await this.app.messageDialog.form(form, this.app.t("customers.formeld.setAbsent.title"), this.app.messageDialog.okCancel) == "ok") {
                        let substituteDuringAbsenceUserName = form.getValue("defaultSubstitute") as boolean ? "" : form.getValue("substitute");
                        await RestEndpoint.main().post().query({ absent: !this.atWork, substituteUserName: substituteDuringAbsenceUserName }).run("api/wfl/workflow/atWork").getString();
                        await this.refresh();
                    }
                }
                finally {
                    this.setAbsentInProgress = false;
                }
            }
            else {
                await RestEndpoint.main().post().query({ absent: !this.atWork }).run("api/wfl/workflow/atWork").getString();
                await this.refresh();
            }
        }
        else {
            await RestEndpoint.main().post().query({ absent: !this.atWork }).run("api/wfl/workflow/atWork").getString();
            await this.refresh();
        }
    }

    async startWorkflow(model: EnaioWorkflowModel): Promise<void> {
        this.loading = true;
        let processId = await RestEndpoint.main().post().query({ familyId: model.familyId}).run("api/wfl/workflow/startProcess").getString();
        this.addTimer(new SmTimer(1000, 1000, async () => {
            let workItemId = await RestEndpoint.main().query({ processId}).run("api/wfl/workflow/processReady").getString();
            if (!Utils.isNoe(workItemId)) {
                this.clearTimers();
                this.loading = false;
                await this.app.navigateTo(["/enaio-workflows", "workitem", processId]);
            }
        }, true));
    }

    async openPersonellFile(): Promise<void> {
        await this.app.navigate(['/enaio-workflows/personell-file']);
    }

    async addSickNote(): Promise<void> {
        let form = this.app.tForm("customers.formeld.addSickNote", new FrontendFormDefinition([
            new FrontendFieldDefinition("self", "", FrontendFieldType.checkBox, { value: true, visible: this.personell.length > 0, onValueChanged: () => {
                form.getField("person").visible = !form.getValue("self");
            }}),
            new FrontendFieldDefinition("person", "", FrontendFieldType.comboBox, { visible: this.personell.length > 0, mandatory: true, dropdownEditable: false,
                listItems: Utils.arraySortBy(this.personell.map(p => new FrontendFieldListItem(p.folderId, p.lastName + ", " + p.firstName)), li => li.label)}),
            new FrontendFieldDefinition("beginDate", "", FrontendFieldType.datePicker, { mandatory: true, dateIntervalToField: "endDate", validator: (field, _) =>
                Utils.dateDiffDays(field.getValue() as Date, new Date()) > 7 ? this.app.t("customers.formeld.addSickNote.beginDate_error1") : null}),
            new FrontendFieldDefinition("endDate", "", FrontendFieldType.datePicker, { mandatory: true, dateIntervalFromField: "beginDate" }),
            new FrontendFieldDefinition("type", "", FrontendFieldType.comboBox, { mandatory: true, dropdownEditable: false, 
                listItems: [ new FrontendFieldListItem("Erstantrag", this.app.t("customers.formeld.addSickNote.type_initial")), new FrontendFieldListItem("Folgeantrag", this.app.t("customers.formeld.addSickNote.type_subsequent")) ] })
        ]));
        form.getField("self").onValueChanged(true);
        if (await this.app.messageDialog.form(form, this.app.t("customers.formeld.addSickNote.title")) == "cancel") {
            return;
        }

        await this.app.saveDataHandler(async () => RestEndpoint.main().post().body(Utils.fromPlain(EnaioCallInsert, {
            archive: EnaioObjectId.byInternalName("Personal"),
            objectType: EnaioObjectId.byInternalName("Krankmeldung"),
            location: new EnaioLocation(null, form.getValue("self") ? this.app.auth.userDetails.getDatabaseUser().getAttributeValueNumber("folderId") : form.getValue("person") as number),
            fields: [
                new EnaioAssignment(EnaioObjectId.byInternalName("Krankmeldungvon"), Utils.dateFormatDefaultDate(form.getValue("beginDate") as Date)),
                new EnaioAssignment(EnaioObjectId.byInternalName("Krankmeldungbis"), Utils.dateFormatDefaultDate(form.getValue("endDate") as Date)),
                new EnaioAssignment(EnaioObjectId.byInternalName("Art"), form.getValue("type"))
            ]
        })).run("api/wfl/tbr/insert").getString(), null, true, false, this.app.t("customers.formeld.addSickNote.success_title"), this.app.t("customers.formeld.addSickNote.success"));
    }

    async addCompensationDay(): Promise<void> {
        let form = this.app.tForm("customers.formeld.addCompensationDay", new FrontendFormDefinition([
            new FrontendFieldDefinition("person", "", FrontendFieldType.comboBox, { mandatory: true, dropdownEditable: false,
                listItems: Utils.arraySortBy(this.personell.filter(p => p.zeitwirtschaft).map(p => new FrontendFieldListItem(p.folderId, p.lastName + ", " + p.firstName)), li => li.label)}),
            new FrontendFieldDefinition("workDate", "", FrontendFieldType.datePicker, { mandatory: true }),
            new FrontendFieldDefinition("compensationDate", "", FrontendFieldType.datePicker, { mandatory: true })
        ]));
        if (await this.app.messageDialog.form(form, this.app.t("customers.formeld.addCompensationDay.title")) == "cancel") {
            return;
        }

        await this.app.saveDataHandler(async () => RestEndpoint.main().post().body(Utils.fromPlain(EnaioCallInsert, {
            archive: EnaioObjectId.byInternalName("Personal"),
            objectType: EnaioObjectId.byInternalName("Ausgleichstag"),
            location: new EnaioLocation(null, form.getValue("person") as number),
            fields: [
                new EnaioAssignment(EnaioObjectId.byInternalName("Arbeitstag"), Utils.dateFormatDefaultDate(form.getValue("workDate") as Date)),
                new EnaioAssignment(EnaioObjectId.byInternalName("Ausgleichstag"), Utils.dateFormatDefaultDate(form.getValue("compensationDate") as Date))
            ]
        })).run("api/wfl/tbr/insert").getString(), null, true, false, this.app.t("customers.formeld.addCompensationDay.success_title"), this.app.t("customers.formeld.addCompensationDay.success"));
    }

}
